import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Clock, User } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { getArticle, articles } from "@/data/articles";

const accentMap = {
    brand: "bg-brand-500",
    pinky: "bg-pinky-500",
    ink: "bg-ink-800",
};

function renderInline(text) {
    // very small **bold** parser
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**") ? (
            <strong key={i} className="font-semibold text-ink-900">
                {p.slice(2, -2)}
            </strong>
        ) : (
            <span key={i}>{p}</span>
        )
    );
}

export default function Article() {
    const { slug } = useParams();
    const article = getArticle(slug);

    if (!article) {
        return (
            <div className="min-h-screen bg-white text-ink-900">
                <Navbar />
                <main className="container-narrow py-32 text-center">
                    <h1 className="display-2">Article introuvable.</h1>
                    <p className="body-lg mt-6">Cet article n'existe pas ou a été déplacé.</p>
                    <Link to="/articles" className="btn-primary mt-10">
                        Voir tous les articles
                    </Link>
                </main>
                <Footer />
            </div>
        );
    }

    const related = articles.filter((a) => a.slug !== article.slug).slice(0, 2);

    return (
        <div className="min-h-screen bg-white text-ink-900">
            <Navbar />
            <article data-testid={`article-page-${article.slug}`} className="pt-32 pb-20">
                <div className="container-narrow">
                    <Link
                        to="/articles"
                        data-testid="article-back-link"
                        className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" strokeWidth={2} /> Tous les articles
                    </Link>

                    <span
                        className={`inline-block mt-8 text-[10px] font-semibold uppercase tracking-[0.18em] ${
                            accentMap[article.accent]
                        } text-white px-3 py-1.5 rounded-full`}
                    >
                        {article.category}
                    </span>

                    <h1 className="display-1 mt-5">{article.title}</h1>

                    <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-ink-500">
                        <span className="inline-flex items-center gap-1.5">
                            <User className="h-4 w-4" strokeWidth={2} /> {article.author}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <Calendar className="h-4 w-4" strokeWidth={2} /> {article.date}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <Clock className="h-4 w-4" strokeWidth={2} /> Lecture · {article.readTime}
                        </span>
                    </div>
                </div>

                <div className="container-narrow mt-12">
                    <div className="relative rounded-[2rem] overflow-hidden ring-soft-brand">
                        <img
                            src={article.img}
                            alt={article.title}
                            className="w-full h-[460px] object-cover"
                            loading="eager"
                        />
                    </div>
                </div>

                <div className="container-narrow mt-14">
                    <div className="prose prose-lg max-w-2xl mx-auto">
                        {article.content.map((block, i) => {
                            if (block.type === "h2") {
                                return (
                                    <h2
                                        key={i}
                                        className="font-display font-bold text-2xl sm:text-3xl text-ink-900 mt-12 mb-5"
                                    >
                                        {block.text}
                                    </h2>
                                );
                            }
                            if (block.type === "list") {
                                return (
                                    <ul key={i} className="mt-6 space-y-3">
                                        {block.items.map((it, j) => (
                                            <li
                                                key={j}
                                                className="flex items-start gap-3 text-ink-700 leading-relaxed"
                                            >
                                                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-brand-500 flex-none" />
                                                <span>{renderInline(it)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                );
                            }
                            return (
                                <p
                                    key={i}
                                    className="text-base sm:text-lg leading-relaxed text-ink-700 mt-6"
                                >
                                    {renderInline(block.text)}
                                </p>
                            );
                        })}
                    </div>
                </div>

                {/* CTA inline */}
                <div className="container-narrow mt-20">
                    <div className="rounded-[2rem] bg-gradient-to-br from-brand-500 to-brand-700 text-white px-8 sm:px-12 py-12 text-center">
                        <h3 className="font-display font-bold text-2xl sm:text-3xl">
                            Envie d'aller plus loin ?
                        </h3>
                        <p className="mt-3 text-brand-50/90">
                            Découvrez le profil neuro-éducatif de votre enfant en 3 minutes.
                        </p>
                        <Link
                            to="/test"
                            data-testid="article-cta-test"
                            className="mt-7 inline-flex items-center justify-center rounded-full bg-white text-brand-700 font-semibold px-7 py-3.5 shadow-lg hover:-translate-y-0.5 transition-all"
                        >
                            Faire le test gratuit
                            <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2.5} />
                        </Link>
                    </div>
                </div>

                {/* Articles connexes */}
                {related.length > 0 && (
                    <div className="container-wide mt-24">
                        <h3 className="font-display font-bold text-2xl sm:text-3xl text-ink-900">
                            À lire ensuite
                        </h3>
                        <div className="mt-8 grid md:grid-cols-2 gap-5">
                            {related.map((a) => (
                                <Link
                                    key={a.slug}
                                    to={`/articles/${a.slug}`}
                                    data-testid={`related-article-${a.slug}`}
                                    className="group soft-card overflow-hidden transition-all hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(15,23,42,0.25)]"
                                >
                                    <div className="relative aspect-[16/9] overflow-hidden">
                                        <img
                                            src={a.img}
                                            alt={a.title}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                        <span
                                            className={`absolute top-4 left-4 inline-block text-[10px] font-semibold uppercase tracking-[0.18em] ${
                                                accentMap[a.accent]
                                            } text-white px-2.5 py-1 rounded-full`}
                                        >
                                            {a.category}
                                        </span>
                                    </div>
                                    <div className="p-6">
                                        <h4 className="font-display font-semibold text-lg text-ink-900">
                                            {a.title}
                                        </h4>
                                        <p className="mt-2 text-sm text-ink-600 leading-relaxed">
                                            {a.excerpt}
                                        </p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </article>
            <Footer />
        </div>
    );
}
